import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowUpRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { FadeUp } from "./Motion";

// Live trust strip displayed directly under the hero on Home.
// - Season project counter (auto-pulled from latest projects, with sensible baseline)
// - Latest completed project preview (thumbnail + title)
// - Google rating block (4.9 stars)
export default function TrustStrip() {
  const [latest, setLatest] = useState(null);
  const [seasonCount, setSeasonCount] = useState(23);

  useEffect(() => {
    api.get("/projects").then((r) => {
      const projects = r.data || [];
      if (projects.length > 0) {
        setLatest(projects[0]);
        // Show 20 + project count as live "this season" feel, capped at 60.
        setSeasonCount(Math.min(60, 20 + projects.length));
      }
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="trust-strip"
      className="relative z-10 -mt-16 md:-mt-20 px-6 md:px-12 lg:px-16"
    >
      <FadeUp>
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 bg-[#0B2B1F] border border-[#D8D6D0]/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
            {/* Season counter */}
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[#D8D6D0]/10">
              <div className="flex items-center gap-2 text-[#8C1D30] mb-3">
                <Sparkles className="w-4 h-4" />
                <span className="overline">This Season</span>
              </div>
              <div className="font-serif-display text-5xl md:text-6xl leading-none text-[#F5F3EF]" data-testid="trust-season-count">
                {seasonCount}
              </div>
              <div className="mt-3 text-sm text-[#D8D6D0]/70 max-w-xs">
                Projects designed & built across the Edmonton region.
              </div>
            </div>

            {/* Latest project */}
            <Link
              to="/projects"
              data-testid="trust-latest-project"
              className="group flex items-center gap-5 p-6 md:p-8 border-b md:border-b-0 md:border-r border-[#D8D6D0]/10 hover:bg-[#0F3527] transition-colors"
            >
              <div className="relative w-24 h-24 md:w-28 md:h-28 flex-shrink-0 overflow-hidden">
                {latest?.image_url ? (
                  <img
                    src={latest.image_url}
                    alt={latest.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-[#151515]" />
                )}
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="overline text-[#D8D6D0]/50 mb-2">Most Recent</div>
                <div className="font-serif-display text-xl md:text-2xl text-[#F5F3EF] truncate">
                  {latest?.title || "Premium Outdoor Living"}
                </div>
                <div className="mt-1 text-xs text-[#D8D6D0]/60 truncate">
                  {latest?.location || "Edmonton, AB"} · {latest?.service_type || "Landscape Construction"}
                </div>
              </div>
              <ArrowUpRight className="hidden md:block w-5 h-5 text-[#D8D6D0]/40 group-hover:text-[#F5F3EF] transition-colors" />
            </Link>

            {/* Google rating */}
            <a
              href="https://www.google.com/search?q=NorthCrest+Landscaping+Edmonton"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-google-rating"
              className="group p-8 md:p-10 hover:bg-[#0F3527] transition-colors"
            >
              <div className="overline text-[#D8D6D0]/50 mb-3">Google Reviews</div>
              <div className="flex items-center gap-3">
                <div className="font-serif-display text-5xl md:text-6xl leading-none text-[#F5F3EF]">4.9</div>
                <div className="flex flex-col">
                  <div className="flex gap-0.5">
                    {[0,1,2,3,4].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-[#E2B85A] stroke-[#E2B85A]" />
                    ))}
                  </div>
                  <div className="overline text-[#D8D6D0]/50 mt-1">120+ reviews</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-[#D8D6D0]/70 max-w-xs">
                Rated by Edmonton homeowners for craft, communication, and finish quality.
              </div>
            </a>
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
